import type { components } from '../../api/schema.gen.ts';
import { apiFetch } from '../../api/http.ts';

type Schemas = components['schemas'];

export type Invitation = Schemas['InvitationResponse'];
export type InvitationPreview = Schemas['InvitationPreviewResponse'];
export type AcceptedInvitation = Schemas['AcceptInvitationResponse'];

export const invitationKeys = {
  mine: ['invitations', 'mine'] as const,
  ofGroup: (groupId: string) => ['invitations', 'group', groupId] as const,
};

/** What is waiting for the signed-in account. */
export function getMyInvitations(): Promise<Invitation[]> {
  return apiFetch<Invitation[]>('/api/invitations/mine');
}

export function getGroupInvitations(groupId: string): Promise<Invitation[]> {
  return apiFetch<Invitation[]>(`/api/groups/${groupId}/invitations`);
}

export function createInvitation(groupId: string, email: string): Promise<Invitation> {
  return apiFetch<Invitation>(`/api/groups/${groupId}/invitations`, {
    method: 'POST',
    json: { email },
  });
}

export function revokeInvitation(groupId: string, invitationId: string): Promise<void> {
  return apiFetch<void>(`/api/groups/${groupId}/invitations/${invitationId}`, {
    method: 'DELETE',
  });
}

/**
 * Accepts by id (from the in-app list) or by token (from an email link). Both resolve to
 * the same invitation on the server, so following the email after already accepting in
 * the app is harmless.
 */
export function acceptInvitation(
  identifier: { invitationId: string } | { token: string },
): Promise<AcceptedInvitation> {
  return apiFetch<AcceptedInvitation>('/api/invitations/accept', {
    method: 'POST',
    json: identifier,
  });
}

export function declineInvitation(invitationId: string): Promise<void> {
  return apiFetch<void>(`/api/invitations/${invitationId}/decline`, { method: 'POST' });
}

/** Readable without an account, so a link can say what it is before asking you to sign up. */
export function previewInvitation(token: string): Promise<InvitationPreview> {
  return apiFetch<InvitationPreview>(
    `/api/invitations/preview?token=${encodeURIComponent(token)}`,
    { anonymous: true },
  );
}
